"use client";

import Section from "@/components/custom/section";
import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { motion } from "motion/react";

export default function WhoWeAre() {
  return (
    <Section className="overflow-x-clip">
      <div className="py-24 sm:py-32">
        <div className="mx-auto">
          <div className="mx-auto grid max-w-2xl grid-cols-1 gap-x-12 gap-y-16 sm:gap-y-20 lg:mx-0 lg:max-w-none lg:grid-cols-2 items-center">
            {/* Text Content */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.7, ease: "easeOut" }}
              className="lg:pt-4 lg:pr-8"
            >
              <div className="lg:max-w-lg space-y-8">
                {/* Badge */}
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.1 }}
                >
                  <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-medium tracking-wide uppercase">
                    <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
                    About Us
                  </span>
                </motion.div>

                {/* Title */}
                <div className="space-y-4">
                  <motion.h2
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: 0.2 }}
                    className="text-4xl font-bold tracking-tight text-pretty text-foreground sm:text-5xl lg:text-6xl leading-tight"
                  >
                    Who we are
                  </motion.h2>
                  <motion.div
                    initial={{ scaleX: 0 }}
                    whileInView={{ scaleX: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: 0.4 }}
                    className="h-1 w-20 bg-linear-to-r from-primary to-secondary rounded-full origin-left"
                  />
                </div>

                {/* Description */}
                <motion.div
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.3 }}
                  className="space-y-6"
                >
                  <p className="text-lg md:text-xl text-muted-foreground leading-relaxed">
                    <strong className="text-foreground">SAL Middle East</strong>, based in Dubai, buys and sells high-quality spare parts for construction machinery at the best prices.
                  </p>
                  <p className="text-lg md:text-xl text-muted-foreground leading-relaxed">
                    Our parts are sourced mainly from <strong className="text-foreground">Italy and Germany</strong> and delivered quickly across the UAE and Middle East.
                  </p>
                </motion.div>

                {/* CTA Button */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.5 }}
                >
                  <Button
                    size="lg"
                    asChild
                    className="group"
                  >
                    <Link href="/who-we-are" title="Learn more about us">
                      Learn more about us
                      <ArrowRight className="size-4 transition-transform group-hover:translate-x-1" />
                    </Link>
                  </Button>
                </motion.div>
              </div>
            </motion.div>

            {/* Image */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.7, ease: "easeOut", delay: 0.2 }}
              className="relative group"
            >
              {/* Decorative frame */}
              <div className="absolute -inset-4 bg-linear-to-br from-primary/20 via-secondary/10 to-transparent rounded-3xl -z-10 -rotate-2" />

              <div
                className="w-full rounded-2xl overflow-hidden shadow-2xl ring-1 ring-border/50 min-h-[400px] lg:min-h-[500px] relative"
              >
                <Image
                  src="/images/dubai-img.jpg"
                  alt="Dubai skyline representing SAL Middle East location"
                  fill
                  className="object-cover transition-transform duration-700 group-hover:scale-105"
                />
                {/* Overlay on hover */}
                <div className="absolute inset-0 bg-linear-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </Section>
  );
}
